/**
 * Defines a container that tiles its children based on an underlying grid.
 *
 * Features include:
 * <ul>
 *    <li>draggable dividers with expanded hit zones</li>
 *    <li>column and row spanning</li>
 *    <li>tile merging and splitting with automatic grid reduction<li>
 *    <li>default row-major tile indexing with column-major option</li>
 *    <li>persistence support</li>
 * </ul>
 *
 * @module MW/layout/TileContainer
 * @copyright Copyright 2015 The MathWorks, Inc.
 */
define([
    "dojo/_base/declare",
    "dojo/_base/event",
    "dojo/_base/lang",
    "dojo/_base/fx",
    "dojo/_base/window",
    "dojo/dom",
    "dojo/dom-class",
    "dojo/dom-construct",
    "dojo/dom-geometry",
    "dojo/dom-style",
    "dojo/Evented",
    "dojo/mouse",
    "dojo/on",
    "dojo/touch",
    "dijit/layout/_LayoutWidget",
    "MW/layout/Geometry",
    "MW/layout/LayoutChildProperty",
    "MW/layout/LayoutContainerEvent",
    "MW/layout/LayoutUtils",
    "MW/layout/Restorable",
    "MW/layout/SelectionBlocker",
    "MW/layout/StatefulExtension",
    "MW/layout/Traversable",
    "mw-log/Log"
], function (declare, event, lang, fx, window, dom, domClass, domConstruct, domGeometry, domStyle, Evented,
             mouse, on, touch, _LayoutWidget, Geometry, LayoutChildProperty, LayoutContainerEvent, LayoutUtils,
             Restorable, SelectionBlocker, StatefulExtension, Traversable, Log) {

    var GRID_SIZE = "gridSize";
    var ROW_WEIGHTS = "rowWeights";
    var COLUMN_WEIGHTS = "columnWeights";
    var TILE_COVERAGE = "tileCoverage";
    var ROW_MAJOR = "rowMajor";
    var COLUMN_MAJOR = "columnMajor";
    var VERTICAL = "vertical";
    var HORIZONTAL = "horizontal";
    var TILE_SPLIT = "tile_split";
    var TILES_MERGED = "tiles_merged";

    /**
     * @class MW/layout/TileContainer
     * @classdesc Defines a layout widget that manages a grid of children
     */
    var TileContainer = declare([_LayoutWidget, Evented, Restorable, StatefulExtension, Traversable], {  /** @lends MW/layout/TileContainer */
        /**
         * @property {{w: number, h: number}} gridSize default is 1 x 1
         * @property {string} indexing
         *    <ul>
         *    <li>"rowMajor" (default)</li>
         *    <li>"columnMajor"</li>
         *    </ul>
         * @property {boolean} offerSplitterRemoveButtons - true if the splitters should display dots which upon hover
         *                                                  become buttons that can be clicked to remove the split
         *
         * @instance
         */
        gridSize: {w: 1, h: 1},
        indexing: ROW_MAJOR,
        minCellHeight: 16,
        minCellWidth: 16,
        offerSplitterRemoveButtons: true,
        baseClass: "tileContainer",

        /* Private properties
        _tileCount
        _children array containing the container's children
        _tileCellBox array containing the box of cells (not pixels) occupied by each tile
        _tileInCell nested array indexed by row and column
        _columnLeft
        _rowTop
        _columnWeight
        _rowWeight
        _columnMajorTile converts from row major
        _rowMajorTile converts from column major
        _horizontalSplitters nested array indexed by row and column
        _verticalSplitters nested array indexed by row and column
        _removeSplitButton
         */

        buildRendering: function () {
            this.inherited(arguments);
            domClass.add(this.domNode, "mwLayoutContainer");
            domClass.add(this.domNode, "mwTileContainer");
        },

        postCreate: function () {
            this.inherited(arguments);
            this._createSplitters();
        },

        startup: function () {
            if (this._started) {return; }

            var that = this;
            this.getChildren().forEach(function (child) {
                that._setupChild(child);
            });

            this.inherited(arguments);
        },

        /**
         * Adds a child to the tile container.  The child will be managed in accordance with the
         * provided properties.  If the properties object is Stateful then the container will update property
         * values as they change and also respond to changes made outside the tile container.
         *
         * @param widget {_Widget} the child to add
         * @param properties {Object} an object containing any of the following properties:
         * @param properties.tile
         *        The tile in which to place the child
         *
         *  @instance
         */
        addWithProperties: function (widget, properties) {
            this._addChildInfo({widget: widget, properties: properties});
            this.addChild(widget);
        },

        /**
         * This method is similar to the above addWithProperties method but assumes that any relevant properties are stored
         * in the widget itself.
         *
         * This method overrides _LayoutWidget.addChild
         *
         * @param widget {_Widget} the child to add
         * @instance
         */
        addChild: function (widget) {
            var childInfo = this._childInfoFromDomId(widget.id);
            if (!childInfo) {
                childInfo = {widget: widget, properties: widget};
                this._addChildInfo(childInfo);
            }
            this.inherited(arguments);
            this.layout();
            this.emit(LayoutContainerEvent.CHILD_ADDED, childInfo.widget, childInfo.properties);
        },

        /**
         * This method overrides _LayoutWidget.removeChild
         *
         * @param widget {_Widget} the widget to remove
         * @instance
         */
        removeChild: function (widget) {
            return this.removeChildInTile(this._tileFromDomId(widget.id));
        },

        removeChildInTile: function(tile, updateLayout) {
            if (updateLayout === undefined) {
                updateLayout = true;
            }
            var removedChild;
            if (tile >= 0) {
                var removedInfo = this._children[tile];
                if (removedInfo) {
                    this._superRemoveChild(removedInfo.widget);
                    this._children[tile] = null;
                    if (updateLayout) {
                        this.layout();
                    }
                    this.emit(LayoutContainerEvent.CHILD_REMOVED, removedInfo.widget, removedInfo.properties);
                    LayoutUtils.restoreStyle(removedInfo.widget);
                    removedChild = removedInfo.widget;
                }
            }
            return removedChild;
        },

        /**
         * Removes (and optionally destroys) all children
         *
         * @param destroy {boolean} true
         * @instance
         */
        removeAll: function (destroy) {
            var tile, child;
            for (tile = 0; tile < this._tileCount; tile++) {
                if (this._children[tile]) {
                    child = this._children[tile].widget;
                    this.removeChildInTile(tile);
                    if (destroy) {
                        child.destroyRecursive();
                    }
                }
            }
        },

        /**
         * Returns the child currently occupying the specified tile, null if the tile is empty
         *
         * @param tile {number} the tile whose child is requested
         * @return {_Widget} the child appearing in the tile (can be null)
         * @instance
         */
        getChildInTile: function (tile) {
            if (tile < 0 || tile >= this._children.length) {
                return null;
            }
            var childInfo = this._children[this._convertToInternalTile(tile)];
            return childInfo ? childInfo.widget : null;
        },

        /**
         * Returns true if the specified tile is unoccupied
         *
         * @param  tile {number} the tile of interest
         * @return {boolean} true if the tile is unoccupied
         * @instance
         */
        isTileEmpty: function (tile) {
            return !this.getChildInTile(tile);
        },

        /**
         * Returns the tile that contains the specified child, -1 if the
         * if the component is not in the TileContainer
         *
         * @param child {_Widget} the child of interest
         * @return {number} the tile containing the child (may be -1)
         * @instance
         */
        getTileContaining: function (child) {
            return this._convertToExternalTile(this._getTileContainingInternal(child));
        },

        /*
         * Returns and array containing the TileContainer children in tile order.  Note that the ordering
         * of the array returned by the inherited getChildren method will not necessarily match the tile order.
         *
         * @returns {Array}
         * @instance
         */
        getChildrenInTileOrder: function () {
            var children = [];
            var tile;
            for (tile = 0; tile < this._tileCount; tile++) {
                if (this._children[tile] && this._children[tile].widget) {
                    children.push(this._children[tile].widget);
                }
            }
            return children;
        },

        _initGrid: function () {
            var row, col, tile;
            this._tileCount = this.gridSize.w * this.gridSize.h;
            this._children = [];
            this._children.length = this._tileCount;
            this._tileCellBox = [];
            this._tileCellBox.length = this._tileCount;
            this._tileInCell = [];
            this._tileInCell.length = this.gridSize.h;
            tile = 0;
            for (row = 0; row < this.gridSize.h; row++) {
                this._tileInCell[row] = [];
                this._tileInCell[row].length = this.gridSize.w;
                for (col = 0; col < this.gridSize.w; col++, tile++) {
                    this._tileInCell[row][col] = tile;
                    this._tileCellBox[tile] = {l:col, t:row, w:1, h:1};
                }
            }
            this._rowTop = [];
            this._rowTop.length = this.gridSize.h + 1;
            this._columnLeft = [];
            this._columnLeft.length = this.gridSize.w + 1;
            // Initialize equal height rows and equal width columns
            this._rowWeight = this._getDefaultWeights(this.gridSize.h);
            this._columnWeight = this._getDefaultWeights(this.gridSize.w);
        },

        _getDefaultWeights: function (count) {
            var weights = [], weight, i;
            weights.length = count;
            weight = 1.0 / count;
            for (i = 0; i < count; i++) {
                weights[i] = weight;
            }
            return weights;
        },

        _createSplitters: function () {
            var that = this;
            var row, col, splitter, line, dot, beginDragFunction, showButtonIfNearDot;
            // First the horizontal splitters.  Rather than a single horizontal splitter for each row the splitters
            // are segmented by column as well.  This is done to support row spanning.
            this._horizontalSplitters = [];
            this._horizontalSplitters.length = this.gridSize.h - 1;
            for (row = 0; row < this.gridSize.h - 1; row++) {
                this._horizontalSplitters[row] = [];
                this._horizontalSplitters[row].length = this.gridSize.w;
                for (col = 0; col < this.gridSize.w; col++) {
                    // Create, configure and listen
                    splitter = domConstruct.create("div", {id : this.id + "HorizontalSplitter" + row + "_" + col}, this.domNode);
                    this._horizontalSplitters[row][col] = splitter;
                    domClass.add(splitter, "splitterH");
                    domClass.add(splitter, "removable");
                    dom.setSelectable(splitter, false);
                    LayoutUtils.preventFocus(splitter, true);
                    LayoutUtils.preventDefaultContextMenu(splitter);

                    line = domConstruct.create("div", {id : this.id + "HorizontalLine" + row + "_" + col}, splitter);
                    domClass.add(line, "splitterLineH");
                    dom.setSelectable(line, false);
                    LayoutUtils.preventFocus(line, true);

                    beginDragFunction = lang.hitch(this, "_beginHorizontalSplitterDrag", row);
                    on(splitter, touch.press, beginDragFunction);
                    on(line, touch.press, beginDragFunction);

                    if (this.offerSplitterRemoveButtons) {
                        dot = domConstruct.create("div", {id : this.id + "HorizontalDot" + row + "_" + col}, line);
                        domClass.add(dot, "dot");
                        dom.setSelectable(dot, false);
                        LayoutUtils.preventFocus(dot, true);

                        showButtonIfNearDot = lang.partial(function (dot, row, col, event) {
                            var dotX = domGeometry.position(dot).x;
                            if (event.pageX >= (dotX - 4) && event.pageX <= (dotX + 5)) {
                                that._showRemoveSplitButton(dot, row, col, "horizontal", event);
                            }
                        }, dot, row, col);
                        on(dot, mouse.enter, lang.hitch(this, "_showRemoveSplitButton", dot, row, col, "horizontal"));
                        on(splitter, "mousemove", showButtonIfNearDot);
                        on(line, "mousemove", showButtonIfNearDot);
                    }
                }
            }

            // Now create the vertical splitters, similarly segmented to support column spanning.
            this._verticalSplitters = [];
            this._verticalSplitters.length = this.gridSize.w - 1;
            for (col = 0; col < this.gridSize.w - 1; col++) {
                this._verticalSplitters[col] = [];
                this._verticalSplitters[col].length = this.gridSize.h;
                for (row = 0; row < this.gridSize.h; row++) {
                    // Create, configure and listen
                    splitter = domConstruct.create("div", {id : this.id + "VerticalSplitter" + row + "_" + col}, this.domNode);
                    this._verticalSplitters[col][row] = splitter;
                    domClass.add(splitter, "splitterV");
                    domClass.add(splitter, "removable");
                    dom.setSelectable(splitter, false);
                    LayoutUtils.preventFocus(splitter, true);
                    LayoutUtils.preventDefaultContextMenu(splitter);

                    line = domConstruct.create("div", {id : this.id + "VerticalLine" + row + "_" + col}, splitter);
                    domClass.add(line, "splitterLineV");
                    dom.setSelectable(line, false);
                    LayoutUtils.preventFocus(line, true);

                    beginDragFunction = lang.hitch(this, "_beginVerticalSplitterDrag", col);
                    on(splitter, touch.press, beginDragFunction);
                    on(line, touch.press, beginDragFunction);

                    if (this.offerSplitterRemoveButtons) {
                        dot = domConstruct.create("div", {id : this.id + "VerticalDot" + row + "_" + col}, line);
                        domClass.add(dot, "dot");
                        dom.setSelectable(dot, false);
                        LayoutUtils.preventFocus(dot, true);

                        showButtonIfNearDot = lang.partial(function (dot, row, col, event) {
                            var dotY = domGeometry.position(dot).y;
                            if (event.pageY >= (dotY - 4) && event.pageY <= (dotY + 5)) {
                                that._showRemoveSplitButton(dot, row, col, "vertical", event);
                            }
                        }, dot, row, col);
                        on(dot, mouse.enter, lang.hitch(this, "_showRemoveSplitButton", dot, row, col, "vertical"));
                        on(splitter, "mousemove", showButtonIfNearDot);
                        on(line, "mousemove", showButtonIfNearDot);
                    }
                }
            }
        },

        _destroySplitters: function () {
            var row, col;
            for (row = 0; row < this._horizontalSplitters.length; row++) {
                for (col = 0; col < this._horizontalSplitters[row].length; col++) {
                    domConstruct.destroy(this._horizontalSplitters[row][col]);
                }
            }
            for (col = 0; col < this._verticalSplitters.length; col++) {
                for (row = 0; row < this._verticalSplitters[col].length; row++) {
                    domConstruct.destroy(this._verticalSplitters[col][row]);
                }
            }
        },

        _refreshSplitters: function () {
            this._destroySplitters();
            this._createSplitters();
        },

        _redistributeChildren: function (savedChildren) {
            if (savedChildren.length === this._children.length) {
                this._children = savedChildren;
                return;
            }
            var count, tile, removeInfo;
            for (count = 0, tile = 0; tile < savedChildren.length; tile++) {
                if (savedChildren[tile]) {
                    if (count < this._children.length) {
                        this._children[count] = savedChildren[tile];
                        count++;
                    } else {
                        removeInfo = savedChildren[tile];
                        if (removeInfo.widget.domNode && removeInfo.widget.getParent()) {
                            this._superRemoveChild(removeInfo.widget);
                            this.emit(LayoutContainerEvent.CHILD_REMOVED, removeInfo.widget, removeInfo.properties);
                            LayoutUtils.restoreStyle(removeInfo.widget);
                        }
                    }
                }
            }
        },

        _showRemoveSplitButton: function (dot, row, col, orientation, event) {
            var that = this;
            var box, connections;
            if (event.buttons) {
                return;
            }
            if (this._removeSplitButton) {
                if (this._removeSplitButton._dot === dot) {
                    return;
                } else {
                    this._removeSplitButton._cleanup();
                }
            }

            var button = domConstruct.create("div", {id : this.id + "RemoveSplitterButton"}, window.body());
            button._dot = dot;
            domClass.add(button, "removeSplitterButton");
            dom.setSelectable(button, false);
            LayoutUtils.preventFocus(button, true);
            box = domGeometry.position(dot);
            box.l = box.x - 6;
            box.t = box.y - 6;
            box.w = 14;
            box.h = 14;
            domGeometry.setMarginBox(button, box);
            button._cleanup = function () {
                domConstruct.destroy(button);
                connections.forEach(function (connection) {
                    connection.remove();
                });
                delete that._removeSplitButton;
            };
            connections = [
                on(button, mouse.leave, button._cleanup),
                on(button, touch.press, function (e) {
                    button._cleanup();
                    if (orientation === "horizontal") {
                        that.mergeTopBottom(row, col);
                    } else {
                        that.mergeLeftRight(row, col);
                    }
                    LayoutUtils.stopEvent(e);
                }),
                on(this.ownerDocument, touch.move, function (event) {
                    if (!Geometry.boxContains(box, event.pageX, event.pageY)) {
                        button._cleanup();
                    }
                })

            ];
            this._removeSplitButton = button;
        },

        _addChildInfo: function (childInfo) {
            this._bindChildProperties(childInfo.widget, childInfo.properties);
            var tile = childInfo.properties.tile;
            if (tile === undefined) {
                tile = this._findFirstEmptyTile();
            } else {
                tile = this._convertToInternalTile(tile);
            }
            Log.assert(tile >= 0 && tile < this._tileCount, "Cannot add child in tile " + tile);
            this._children[tile] = childInfo;
            childInfo.properties.set(LayoutChildProperty.TILE, tile);

        },

        _setupChild: function (child) {
            this.inherited(arguments);
            domStyle.set(child.domNode, "position", "absolute");
        },

        _bindChildProperties: function (widget, properties) {
            // TODO: Bind to tile and move in response to changes
        },

        _findFirstEmptyTile: function () {
            var tile;
            for (tile = 0; tile < this._tileCount; tile++) {
                if (!this._children[tile]) {
                    break;
                }
            }
            return tile;
        },

        _childInfoFromDomId: function (id) {
            var tile = this._tileFromDomId(id);
            return tile >= 0 ? this._children[tile] : null;
        },

        _tileFromDomId: function (id) {
            var tile;
            for (tile = 0; tile < this._tileCount; tile++) {
                if (this._children[tile] && this._children[tile].widget.id === id) {
                    break;
                }
            }
            return tile < this._tileCount ? tile : -1;
        },

        /**
         * Updates the container in response to resize.
         *
         * This method overrides the inherited resize method
         *
         * @instance
         */
        resize: function () {
            // Cache the computed style
            if (!this.computedStyle) {
                this.computedStyle = domStyle.getComputedStyle(this.domNode);
            }
            this.inherited(arguments);
        },

        /**
         * Positions the children
         *
         * This method overrides the inherited layout method
         *
         * @instance
         */
        layout: function () {
            var contentBox, availHeight, availWidth, row, col, nextRow, nextCol, splitterBox, tile;
            if (!this._started || !this.domNode) {
                return;
            }
            contentBox = domGeometry.getContentBox(this.domNode, this.computedStyle);
            if (!contentBox || contentBox.h === 0) {
                return;  // No height
            }

            // Compute row tops and column lefts
            if (this._referenceHeight !== contentBox.h) {
                this._referenceHeight = contentBox.h;
                availHeight = contentBox.h - (this.gridSize.h - 1) * this._getVisibleSplitterThickness();
                this._rowTop[0] = contentBox.t;
                for (row = 0; row < this.gridSize.h - 1; row = nextRow) {
                    nextRow = row + 1;
                    this._rowTop[nextRow] = this._rowTop[row] + Math.round(this._rowWeight[row] * availHeight);
                }
                this._rowTop[this.gridSize.h] = contentBox.t + contentBox.h;
            }
            if (this._referenceWidth !== contentBox.w) {
                this._referenceWidth = contentBox.w;
                availWidth = contentBox.w - (this.gridSize.w - 1) * this._getVisibleSplitterThickness();
                this._columnLeft[0] = contentBox.l;
                for (col = 0; col < this.gridSize.w - 1; col = nextCol) {
                    nextCol = col + 1;
                    this._columnLeft[nextCol] = this._columnLeft[col] + Math.round(this._columnWeight[col] * availWidth);
                }
                this._columnLeft[this.gridSize.w] = contentBox.l + contentBox.w;
            }

            // Position the splitters
            splitterBox = {l: 0, t: 0, w: 0, h: this._getSplitterThickness()};
            for (row = 0; row < this.gridSize.h - 1; row++) {
                splitterBox.t = this._rowTop[row + 1] - this._getPreSplitterOverlap();
                 for (col = 0; col < this.gridSize.w; col++) {
                     splitterBox.l = this._columnLeft[col];
                     splitterBox.w = this._columnLeft[col + 1] - splitterBox.l + 1;
                     domGeometry.setMarginBox(this._horizontalSplitters[row][col], splitterBox);
                 }
            }
            splitterBox.w = this._getSplitterThickness();
            for (col = 0; col < this.gridSize.w - 1; col++) {
                splitterBox.l = this._columnLeft[col + 1] - this._getPreSplitterOverlap();
                for (row = 0; row < this.gridSize.h; row++) {
                    splitterBox.t = this._rowTop[row];
                    splitterBox.h = this._rowTop[row + 1] - splitterBox.t + 1;
                    domGeometry.setMarginBox(this._verticalSplitters[col][row], splitterBox);
                }
            }

            // Position the children
            for (tile = 0; tile < this._tileCount; tile++) {
                if (this._children[tile]) {
                    LayoutUtils.setSize(this._children[tile].widget, this.getTilePixelBox(tile));
                }
            }
        },

        _getSplitterThickness: function () {
            if (!this._splitterThickness) {
                this._measureSplitters();
            }
            return this._splitterThickness || 8;
        },

        _getVisibleSplitterThickness: function () {
            if (!this._visibleSplitterThickness) {
                this._measureSplitters();
            }
            return this._visibleSplitterThickness || 1;
        },

        _getPreSplitterOverlap: function () {
            if (!this._preSplitterOverlap) {
                this._measureSplitters();
            }
            return this._preSplitterOverlap || 2;
        },

        _measureSplitters: function () {
            var splitter;
            if (this._horizontalSplitters.length) {
                splitter = this._horizontalSplitters[0][0];
                this._splitterThickness = Math.round(domStyle.get(splitter, "height"));
                this._visibleSplitterThickness = Math.round(domStyle.get(splitter.firstChild, "height"));
                this._preSplitterOverlap = Math.round(domStyle.get(splitter, "padding-top"));
            } else if (this._verticalSplitters.length) {
                splitter = this._verticalSplitters[0][0];
                this._splitterThickness = Math.round(domStyle.get(splitter, "width"));
                this._visibleSplitterThickness = Math.round(domStyle.get(splitter.firstChild, "width"));
                this._preSplitterOverlap = Math.round(domStyle.get(splitter, "padding-left"));
            }
        },
        /**
         * Determines the coordinates of the specified tile
         *
         * @param tile {number} the tile of interest
         * @return {{l: number, t: number, w: number, h: number}} the box occupied by the tile
         */
        getTilePixelBox: function (tile) {
            var cellBox = this._tileCellBox[tile];
            var endColumn = cellBox.l + cellBox.w;
            var endRow = cellBox.t + cellBox.h;
            var left   = this._columnLeft[cellBox.l];
            var right  = this._columnLeft[endColumn];
            var top    = this._rowTop[cellBox.t];
            var bottom = this._rowTop[endRow];
            if (endColumn < this.gridSize.w) {
                right -= this._getVisibleSplitterThickness();
            }
            if (endRow < this.gridSize.h) {
                bottom -= this._getVisibleSplitterThickness();
            }
            return {l: left, t: top, w: right - left, h: bottom - top};
        },

        _getCellFromPoint: function (x, y) {
            var cell = {row: this._getRowFromY(y),
                        column: this._getColumnFromX(x)};
            return cell.row < 0 || cell.column < 0 ? null : cell;
        },

        _getRowFromY: function (y) {
            var row;
            for (row = -1; row < this.gridSize.h; row++) {
                if (y < this._rowTop[row + 1]) {
                    return row;
                }
            }
            return -1;
        },

        _getColumnFromX: function (x) {
            var col;
            for (col = -1; col < this.gridSize.w; col++) {
                if (x < this._columnLeft[col + 1]) {
                    return col;
                }
            }
            return -1;
        },

        /**
         * Same as getTileContaining but the internal, row major, tile indexing is assumed.
         *
         * @param child {_Widget} the child of interest
         * @return {number} the tile containing the child (may be -1)
         */
        _getTileContainingInternal: function (child) {
            var tile;
            for (tile = 0; tile < this._tileCount && this._children[tile].widget !== child; tile++);
            return (tile === this._tileCount) ? -1 : tile;
        },

        /**
         * Converts a tile index from its external representation (which may be column major
         * or row major) to its internal representation (which is always row major).
         *
         * @param tile {number} the external tile index
         * @return {number} the corresponding internal tile index
         */
        _convertToInternalTile: function (tile) {
            return (this.indexing === COLUMN_MAJOR && tile >= 0) ? this._convertToColumnMajor(tile) : tile;
        },

        /**
         * Converts a tile index from its internal representation (which is always row major)
         * to its external representation (which may be column major or row major).
         *
         * @param tile {number} the internal tile index
         * @return {number}the corresponding external tile index
         */
        _convertToExternalTile: function (tile) {
            return (this.indexing === COLUMN_MAJOR && tile >= 0) ? this._convertToRowMajor(tile) : tile;
        },

        /**
         * Converts a row major tile to a column major tile
         *
         * @param rowMajorTile {number} the row major tile
         * @return {number} the corresponding column major tile
         */
        _convertToColumnMajor: function (rowMajorTile) {
            if (!this._columnMajorTile) {
                this._computeTileConversionTables();
            }
            return this._columnMajorTile[rowMajorTile];
        },

        /**
         * Converts a column major tile to a row major tile
         *
         * @param columnMajorTile {number} the column major tile
         * @return {number} the corresponding row major tile
         */
        _convertToRowMajor: function (columnMajorTile) {
            if (!this._rowMajorTile) {
                this._computeTileConversionTables();
            }
            return this._rowMajorTile[columnMajorTile];
        },

        _computeTileConversionTables: function () {
            var tile, row, col, columnMajorCount, rowMajorTile;
            this._columnMajorTile = [];
            this._columnMajorTile.length = this._tileCount;
            this._rowMajorTile = [];
            this._rowMajorTile.length = this._tileCount;
            for (tile = 0; tile < this._tileCount; tile++) {
                this._columnMajorTile[tile] = -1;
                this._rowMajorTile[tile] = -1;
            }

            columnMajorCount = 0;
            for (col = 0; col < this.gridSize.w; col++) {
                for (row = 0; row < this.gridSize.h; row++) {
                    rowMajorTile = this._tileInCell[row][col];
                    if (this._columnMajorTile[rowMajorTile] < 0) {
                        this._columnMajorTile[rowMajorTile]  = columnMajorCount;
                        this._rowMajorTile[columnMajorCount] = rowMajorTile;
                        columnMajorCount++;
                    }
                }
            }
        },

        _clearTileConversionTables: function () {
            delete this._columnMajorTile;
            delete this._rowMajorTile;
        },

        _beginHorizontalSplitterDrag: function (splitterIndex, e) {
            if (mouse.isLeft(e) && !this._splitterDragInfo) {
                this._splitterDragInfo = {
                    splitterIndex: splitterIndex,
                    startY: e.pageY,
                    initialPosition: this._rowTop[splitterIndex + 1],
                    localLowerLimit: this._getSplitterTopLocalLowerLimit(splitterIndex, this._rowTop),
                    localUpperLimit: this._getSplitterTopLocalUpperLimit(splitterIndex, this._rowTop),
                    globalLowerLimit: this._getSplitterTopGlobalLowerLimit(splitterIndex),
                    globalUpperLimit: this._getSplitterTopGlobalUpperLimit(splitterIndex),
                    connections: [
                        on(this.ownerDocument, touch.move, lang.hitch(this, "_continueHorizontalSplitterDrag")),
                        on(this.ownerDocument, touch.release, lang.hitch(this, "_endSplitterDrag"))
                    ]
                };
                SelectionBlocker.blockUntilRelease(this.domNode);
            }
            LayoutUtils.stopEvent(e);
        },

        _continueHorizontalSplitterDrag: function (e) {
            var dragInfo    = this._splitterDragInfo;
            var isGlobal    = !!e.ctrlKey;
            var offset      = e.pageY - dragInfo.startY;
            this._rowTop[dragInfo.splitterIndex + 1] = this._enforceDragLimits(dragInfo.initialPosition + offset, dragInfo, isGlobal);
            if (isGlobal) {
                // Adjust other splitters proportionally
                this._scaleCells(dragInfo.splitterIndex, this.gridSize.h, this._rowTop, this._rowWeight);
                // Recompute the local limits
                dragInfo.localLowerLimit = this._getSplitterTopLocalLowerLimit(dragInfo.splitterIndex, this._rowTop);
                dragInfo.localUpperLimit = this._getSplitterTopLocalUpperLimit(dragInfo.splitterIndex, this._rowTop);
            }
            this.layout();
        },

        _endSplitterDrag: function () {
            this._splitterDragInfo.connections.forEach(function (connection) {
                connection.remove();
            });
            delete this._splitterDragInfo;
            this._updateWeights();
            this.emit(LayoutContainerEvent.STATE_CHANGED);
        },

        _beginVerticalSplitterDrag: function (splitterIndex, e) {
            if (mouse.isLeft(e) && !this._splitterDragInfo) {
                this._splitterDragInfo = {
                    splitterIndex: splitterIndex,
                    startX: e.pageX,
                    initialPosition: this._columnLeft[splitterIndex + 1],
                    localLowerLimit: this._getSplitterLeftLocalLowerLimit(splitterIndex, this._columnLeft),
                    localUpperLimit: this._getSplitterLeftLocalUpperLimit(splitterIndex, this._columnLeft),
                    globalLowerLimit: this._getSplitterLeftGlobalLowerLimit(splitterIndex),
                    globalUpperLimit: this._getSplitterLeftGlobalUpperLimit(splitterIndex),
                    connections: [
                        on(this.ownerDocument, touch.move, lang.hitch(this, "_continueVerticalSplitterDrag")),
                        on(this.ownerDocument, touch.release, lang.hitch(this, "_endSplitterDrag"))
                    ]
                };
                SelectionBlocker.blockUntilRelease(this.domNode);
            }
            LayoutUtils.stopEvent(e);
        },

        _continueVerticalSplitterDrag: function (e) {
            var dragInfo    = this._splitterDragInfo;
            var isGlobal    = !!e.ctrlKey;
            var offset      = e.pageX - dragInfo.startX;
            this._columnLeft[dragInfo.splitterIndex + 1] = this._enforceDragLimits(dragInfo.initialPosition + offset, dragInfo, isGlobal);
            if (isGlobal) {
                // Adjust other splitters proportionally
                this._scaleCells(dragInfo.splitterIndex, this.gridSize.w, this._columnLeft, this._columnWeight);
                // Recompute the local limits
                dragInfo.localLowerLimit = this._getSplitterLeftLocalLowerLimit(dragInfo.splitterIndex, this._columnLeft);
                dragInfo.localUpperLimit = this._getSplitterLeftLocalUpperLimit(dragInfo.splitterIndex, this._columnLeft);
            }
            this.layout();
        },

        _enforceDragLimits: function (newPosition, dragInfo, isGlobal) {
            if (isGlobal) {
                if (newPosition < dragInfo.globalLowerLimit) {
                    newPosition = dragInfo.globalLowerLimit;
                } else if (newPosition > dragInfo.globalUpperLimit) {
                    newPosition = dragInfo.globalUpperLimit;
                }
            } else {
                if (newPosition < dragInfo.localLowerLimit) {
                    newPosition = dragInfo.localLowerLimit;
                } else if (newPosition > dragInfo.localUpperLimit) {
                    newPosition = dragInfo.localUpperLimit;
                }
            }
            return newPosition;
        },

        _scaleCells: function (splitterIndex, cellCount, cellEdges, cellWeights) {
            var availSize, cellSizes, totalWeight, cellIndex;
            cellSizes = [];
            cellSizes.length = cellCount;
            if (splitterIndex > 0) {
                availSize = cellEdges[splitterIndex + 1] - cellEdges[0];
                totalWeight = 0;
                for (cellIndex = 0; cellIndex < splitterIndex + 1; cellIndex++) {
                    cellSizes[cellIndex] = cellWeights[cellIndex] * availSize;
                    totalWeight += cellWeights[cellIndex];
                }
                for (cellIndex = 0; cellIndex < splitterIndex; cellIndex++) {
                    cellEdges[cellIndex + 1] = cellEdges[cellIndex] + cellSizes[cellIndex] / totalWeight;
                }
            }
            if (splitterIndex < cellCount - 2) {
                availSize = cellEdges[cellCount] - cellEdges[splitterIndex + 1];
                totalWeight = 0;
                for (cellIndex = splitterIndex + 1; cellIndex < cellCount; cellIndex++) {
                    cellSizes[cellIndex] = cellWeights[cellIndex] * availSize;
                    totalWeight += cellWeights[cellIndex];
                }
                for (cellIndex = splitterIndex + 1; cellIndex < cellCount; cellIndex++) {
                    cellEdges[cellIndex + 1] = cellEdges[cellIndex] + cellSizes[cellIndex] / totalWeight;
                }
            }
        },

        /**
         * Sets the grid dimensions used to tile the child components.
         *
         * @param gridSize {{w: number, h: number}} the tiling dimensions
         */
        _setGridSizeAttr: function (gridSize) {
            var oldTileCount = this._tileCount;
            if (oldTileCount && this.gridSize.w === gridSize.w && this.gridSize.h === gridSize.h) {
                return;
            }
            this._isSettingGridSize = true;
            var savedChildren, saveInfo;
            if (oldTileCount) {
                saveInfo = this._prepareForGridSizeChange(gridSize);
                savedChildren = this._children;
            }
            var oldGridSize = this.gridSize;
            this.gridSize = lang.clone(gridSize);
            this._initGrid();
            if (oldTileCount) {
                this._clearTileConversionTables();
                this._refreshSplitters();
                this._redistributeChildren(savedChildren);
                this._reactToGridSizeChange(saveInfo);
            }
            this._referenceHeight = 0;  // Force recomputation of row and column positions
            this._referenceWidth = 0;
            this.layout();
            this._queueGridSizeChangeNotification(oldGridSize, gridSize);
            this._flushPendingNotifications();
            this._isSettingGridSize = false;
        },

        /**
         * Returns the number of tiles into which the TiledPane has been
         * partitioned.  This may be less than the product of the grid dimensions
         * because tiles may span multiple grid cells
         *
         * @return the number of tiles
         */
        _getTileCountAttr: function () {
            return this._tileCount;
        },

        /**
         * Returns the box of grid cells occupied by the given tile
         *
         * @param tile {number} the tile of interest
         * @return {{l: number, t: number, w: number, h: number}} the cell box
         */
        getTileCellBox: function (tile) {
            return this._tileCellBox[this._convertToInternalTile(tile)];
        },

        /**
         * Computes the adjacency relationship between two tiles.  Returing:
         *    "left" if tile1 is immediately to the left of tile2
         *    "right" if tile1 is immediately to the right of tile2
         *    "top" if tile1 is immediately above tile2
         *    "bottom" if tile1 is immediately below tile2
         *    "" otherwise
         *
         * @param tile1
         * @param tile2
         */
        getAdjacency: function (tile1, tile2) {
            var box1 = this._tileCellBox[tile1];
            var box2 = this._tileCellBox[tile2];
            if (tile1 < tile2) {
                if (box1.l + box1.w === box2.l) {
                    return "left";
                }
                if (box1.t + box1.h === box2.t) {
                    return "top"
                }
            } else if (tile1 > tile2) {
                if (box2.l + box2.w === box1.l) {
                    return "right";
                }
                if (box2.t + box2.h === box1.t) {
                    return "bottom";
                }
            }
            return "";
        },

        /**
         * Sets the fractional column widths.  If the length of the supplied array is less than
         * the number of columns, the balance of the available space is distributed evenly among
         * the remaining columns.
         *
         * @param weights {[number]} an array containing the desired column weights
         */
        _setColumnWeightsAttr: function (weights) {
            var totalWeight = 0;
            var col;
            for (col = 0; col < Math.min(weights.length, this.gridSize.w - 1) && totalWeight < 1; col++) {
                this._columnWeight[col] = weights[col];
                totalWeight += weights[col];
            }
            var remainingColumns = this.gridSize.w - col;
            var remainingWidth   = Math.max(1 - totalWeight, 0);
            var share = remainingWidth / remainingColumns;
            for (; col < this.gridSize.w; col++) {
                this._columnWeight[col] = share;
            }
            this._referenceWidth = 0;  // Force recomputation of column lefts
            this.layout();
            this.notifyStateWatchers(COLUMN_WEIGHTS, [], this._columnWeight);
        },

        /**
         * @returns {Array} an array containing the column weights
         */
        _getColumnWeightsAttr: function () {
            return this._columnWeight.slice(0, this.gridSize.w);
        },

        /**
         * Sets the fractional row heights.  If the length of the supplied array is less than
         * the number of rows, the balance of the available space is distributed evenly among
         * the remaining rows.
         *
         * @param weights {[number]} an array containing the desired row weights
         */
        _setRowWeightsAttr: function (weights) {
            var totalWeight = 0;
            var row;
            for (row = 0; row < Math.min(weights.length, this.gridSize.h - 1) && totalWeight < 1; row++) {
                this._rowWeight[row] = weights[row];
                totalWeight += weights[row];
            }
            var remainingRows   = this.gridSize.h - row;
            var remainingHeight = Math.max(1 - totalWeight, 0);
            var share = remainingHeight / remainingRows;
            for (; row < this.gridSize.h; row++) {
                this._rowWeight[row] = share;
            }
            this._referenceHeight = 0;  // Force recomputation of row tops
            this.layout();
            this.notifyStateWatchers(ROW_WEIGHTS, [], this._rowWeight);
        },

        /**
         * @returns {Array} an array containing the row weights
         */
        _getRowWeightsAttr: function () {
            return this._rowWeight.slice(0, this.gridSize.h);
        },

        /**
         * Adjusts the tiling such that the tile at the given position spans the specified
         * number of columns.  If the tile is being expanded (increasing its span) the tiles
         * currently occupying the newly spanned columns must all have the same height as the
         * that tile.
         *
         * @param row  {number} the row in which the multi-column tile appears
         * @param col  {number} the first column in which the tile appears
         * @param span {number} the number of columns the tile spans
         */
        setColumnSpan: function (row, col, span) {
            this._checkSpanParameters(row, col, span);

            var that = this;
            var tile, heightsMatch, columnsNeeded, overtakenTiles, overtakenCount, mergeData, width, newTile, excessWidth;
            var tileToAdjust = this._tileInCell[row][col];
            var externalToAdjust = this._convertToExternalTile(tileToAdjust);
            var externalNeighbor = this._convertToExternalTile(tileToAdjust + 1);

            var tileBox = this._tileCellBox[tileToAdjust];
            if (tileBox.w === span) {
                return;  // Already has desired span
            }

            if (tileBox.w < span) {
                // Expand the tile
                // Don't let span exceed grid
                if (tileBox.l + span > this.gridSize.w)
                    span = this.gridSize.w - tileBox.l;

                // Make sure the adjacent tiles have the same height.
                heightsMatch = true;
                for (col = tileBox.l + tileBox.w; col < tileBox.l + span && heightsMatch; ) {
                    tile = this._tileInCell[tileBox.t][col];
                    heightsMatch = this._tileCellBox[tile].h === tileBox.h;
                    col += this._tileCellBox[tile].w;
                }
                if (!heightsMatch) {
                    return;
                }

                // Determine how many adjacent tiles to take over
                columnsNeeded  = span - tileBox.w;
                overtakenTiles = [];
                for (tile = tileToAdjust + 1; columnsNeeded > 0; tile++) {
                    width = this._tileCellBox[tile].w;
                    if (width <= columnsNeeded) {
                        columnsNeeded -= width;
                        overtakenTiles.push(tile);
                    } else {
                        this._tileCellBox[tile].l += columnsNeeded;
                        this._tileCellBox[tile].w -= columnsNeeded;
                        columnsNeeded = 0;
                    }
                }
                overtakenCount = overtakenTiles.length;

                // Adjust the tile dimensions and record its cell occupancy
                tileBox.w = span;
                this._recordOccupyingTile(tileBox, tileToAdjust);

                if (overtakenCount > 0) {
                    // Give subclasses a chance to prepare before removing children
                    mergeData = this._prepareForMerge(tileToAdjust, overtakenTiles);
                    overtakenTiles.forEach (function (tile) {
                        that.removeChildInTile(tile, false);
                    });

                    // Update subsequent tiles
                    for (tile = tileToAdjust + 1; tile < this._tileCount - overtakenCount; tile++) {
                        this._children   [tile] = this._children   [tile + overtakenCount];
                        this._tileCellBox[tile] = this._tileCellBox[tile + overtakenCount];
                        this._recordOccupyingTile(this._tileCellBox[tile], tile);
                    }
                    this._tileCount -= overtakenCount;
                }
                this._coalesceColumn(tileBox.l);

                if (overtakenCount > 0) {
                    this._reactToMerge(tileToAdjust, overtakenTiles, mergeData);
                    this.emit(TILES_MERGED, externalToAdjust, externalNeighbor);
                    this._pendingStateChangeNotification = true;
                }
            } else {
                // Split the tile
                newTile     = tileToAdjust + 1;
                excessWidth = tileBox.w - span;
                tileBox.w   = span;

                // Make room for the new tile by shifting data and updating tile numbers
                for (tile = this._tileCount - 1; tile >= newTile; tile--)
                {
                    this._children[tile + 1] = this._children[tile];
                    this._tileCellBox[tile + 1] = this._tileCellBox[tile];
                    this._recordOccupyingTile(this._tileCellBox[tile + 1], tile + 1);
                }
                this._tileCellBox[newTile] = {
                    l: tileBox.l + span,
                    t: tileBox.t,
                    w: excessWidth,
                    h: tileBox.h
                };
                this._recordOccupyingTile(this._tileCellBox[newTile], newTile);
                this._tileCount++;
                this.emit(TILE_SPLIT, externalToAdjust, VERTICAL);
                this._pendingStateChangeNotification = true;
            }
            this._updateSplitterVisibility();
            this.layout();
            this._flushPendingNotifications();
        },

        /**
         * Adjusts the tiling such that the tile at the given position spans the specified
         * number of rows.  If the tile is being expanded (increasing its span) the tiles
         * currently occupying the newly spanned rows must all have the same width as the
         * that tile.
         *
         * @param row  {number} the first row in which the tile appears
         * @param col  {number} the col in which the multi-row tile appears
         * @param span {number} the number of rows the tile spans
         */
        setRowSpan: function (row, col, span) {
            this._checkSpanParameters(row, col, span);
            var that = this;
            var tile, widthsMatch, rowsNeeded, overtakenTiles, overtakenCount, overtakenIndex, externalFirstOvertaken;
            var mergeData, height, newTile, excessHeight;
            var tileToAdjust  = this._tileInCell[row][col];
            var externalToAdjust = this._convertToExternalTile(tileToAdjust);

            var tileBox = this._tileCellBox[tileToAdjust];
            if (tileBox.h === span)
                return;  // Already has desired span

            if (tileBox.h < span) {
                // Expand the tile
                // Don't let span exceed grid
                if (tileBox.t + span > this.gridSize.h) {
                    span = this.gridSize.h - tileBox.t;
                }

                // Make sure the tiles to be merged have the same width.
                widthsMatch = true;
                for (row = tileBox.t + tileBox.h; row < tileBox.t + span && widthsMatch; ) {
                    tile = this._tileInCell[row][tileBox.l];
                    widthsMatch = this._tileCellBox[tile].w === tileBox.w;
                    row += this._tileCellBox[tile].h;
                }
                if (!widthsMatch) {
                    return;
                }

                // Determine how many tiles to take over
                rowsNeeded = span - tileBox.h;
                overtakenTiles = [];
                overtakenTiles.length = rowsNeeded;
                externalFirstOvertaken = -1;
                overtakenCount = 0;
                for (row = tileBox.t + tileBox.h; row < tileBox.t + span; ) {
                    tile = this._tileInCell[row][tileBox.l];
                    height = this._tileCellBox[tile].h;
                    if (height <= rowsNeeded) {
                        rowsNeeded -= height;
                        if (overtakenCount === 0) {
                            externalFirstOvertaken = this._convertToExternalTile(tile);
                        }
                        overtakenTiles[overtakenCount++] = tile;
                    } else {
                        this._tileCellBox[tile].t += rowsNeeded;
                        this._tileCellBox[tile].h -= rowsNeeded;
                        rowsNeeded = 0;
                    }
                    row += this._tileCellBox[tile].h;
                }

                // Adjust the tile dimensions and record its cell occupancy
                tileBox.h = span;
                this._recordOccupyingTile(tileBox, tileToAdjust);

                if (overtakenCount > 0) {
                    // Give subclasses a chance to prepare before removing children
                    mergeData = this._prepareForMerge(tileToAdjust, overtakenTiles);
                    overtakenTiles.forEach (function (tile) {
                        that.removeChildInTile(tile, false);
                    });

                    // Update subsequent tiles
                    for (tile = tileToAdjust + 1, overtakenIndex = 0; tile < this._tileCount - overtakenCount; tile++) {
                        if (overtakenIndex < overtakenCount && (tile + overtakenIndex) === overtakenTiles[overtakenIndex]) {
                            overtakenIndex++;
                        }
                        this._children[tile] = this._children[tile + overtakenIndex];
                        this._tileCellBox[tile] = this._tileCellBox[tile + overtakenIndex];
                        this._recordOccupyingTile(this._tileCellBox[tile], tile);
                    }
                    this._tileCount -= overtakenCount;
                }
                this._coalesceRow(tileBox.t);

                if (overtakenCount > 0) {
                    this._reactToMerge(tileToAdjust, overtakenTiles, mergeData);
                    this.emit(TILES_MERGED, externalToAdjust, externalFirstOvertaken);
                    this._pendingStateChangeNotification = true;
                }
            } else {
                // Split the tile
                newTile = this._tileInCell[tileBox.t + span][tileBox.l + tileBox.w];
                excessHeight = tileBox.h - span;
                tileBox.h  = span;

                // Make room for the new tile by shifting data and updating tile numbers
                for (tile = this._tileCount - 1; tile >= newTile; tile--) {
                    this._children[tile + 1] = this._children[tile];
                    this._tileCellBox[tile + 1] = this._tileCellBox[tile];
                    this._recordOccupyingTile(this._tileCellBox[tile + 1], tile + 1);
                }
                this._tileCellBox[newTile] = {
                    l: tileBox.l ,
                    t: tileBox.t + span,
                    w: tileBox.w,
                    h: excessHeight
                };
                this._recordOccupyingTile(this._tileCellBox[newTile], newTile);
                this._tileCount++;

                this.emit(TILE_SPLIT, externalToAdjust, HORIZONTAL);
                this._pendingStateChangeNotification = true;
            }
            this._updateSplitterVisibility();
            this.layout();
            this._flushPendingNotifications();
        },

        _checkSpanParameters: function (row, col, span) {
            Log.assert(row < this.gridSize.h, "Row = " + row + " exceeds grid height = " + this.gridSize.h);
            Log.assert(col < this.gridSize.w, "Column = " + col + " exceeds grid width = " + this.gridSize.w);
            Log.assert(span >= 1, "Span = " + span +  " must be at least 1");
        },

        /**
         * Eliminates a tile, if possible, by expanding adjacent tiles.  In some case elimination may
         * not be possible due to a lack of adjacent tiles spanning the same dimension.
         *
         * @param tileToEliminate {number} the tile to eliminate
         * @param heir {number} the tile that should preferrably acquire the freed up real-estate.
         * @return {_Widget} the child removed (if any)
         */
        eliminateTile: function (tileToEliminate, heir) {
            tileToEliminate = this._convertToInternalTile(tileToEliminate);
            heir = this._convertToInternalTile(heir);

            // Direction constants in order of preferred expansion
            var directions = ["east", "south", "west", "north"];
            var elimBox = this._tileCellBox[tileToEliminate];
            var heirBox, heirDirection, nextDirection, tempDirection, expansionDirection, i, col, row, tile, eliminateInfo;

            // Update the directions array to give preference to the heir's direction
            if (heir >= 0) {
                // First determine the heir's direction
               heirBox = this._tileCellBox[heir];
                if ((heirBox.l + heirBox.w) <= elimBox.l)
                    heirDirection = "west";
                else if ((heirBox.t + heirBox.h) <= elimBox.t)
                    heirDirection = "north";
                else if (heirBox.l >= (elimBox.l + elimBox.w))
                    heirDirection = "east";
                else if (heirBox.t >= (elimBox.t + elimBox.h))
                    heirDirection = "south";
                if (heirDirection) {
                    // Now update the array, putting the heir's direction first
                    nextDirection = heirDirection;
                    for (i = 0; directions[i] !== heirDirection; i++) {
                        tempDirection = directions[i];
                        directions[i] = nextDirection;
                        nextDirection = tempDirection;
                    }
                    directions[i] = nextDirection;
                }
            }

            // Check each direction looking for a set of adjacent tiles that span the same number of cells
            // along the perpendicular as the tile being eliminated.  This is the necessary condition
            // to be able to expand the adjacent tiles to fill the cells occupied by the tile being
            // eliminated.
            for (i = 0; i < directions.length && !expansionDirection; i++) {
                switch(directions[i]) {
                    case "north":
                        if (elimBox.t > 0) {
                            var tileAbove = this._tileInCell[elimBox.t - 1][elimBox.l];
                            if (this._tileCellBox[tileAbove].l !== elimBox.l) {
                                break;
                            }
                            tileAbove = this._tileInCell[elimBox.t - 1][elimBox.l + elimBox.w - 1];
                            var boxAbove = this._tileCellBox[tileAbove];
                            if ((boxAbove.l + boxAbove.w) === (elimBox.l + elimBox.w)) {
                                expansionDirection = "north";
                                // Expand all of the tiles above
                                for (col = elimBox.l; col < (elimBox.l + elimBox.w); col++) {
                                    if (this._tileInCell[elimBox.t][col] === tileToEliminate) {
                                        tileAbove = this._tileInCell[elimBox.t - 1][col];
                                        boxAbove  = this._tileCellBox[tileAbove];
                                        boxAbove.h += elimBox.h;
                                        this._recordOccupyingTile(boxAbove, tileAbove);
                                    }
                                }
                                this._coalesceRow(elimBox.t - 1);
                            }
                        }
                        break;
                    case "south":
                        if ((elimBox.t + elimBox.h) < this.gridSize.h) {
                            var tileBelow = this._tileInCell[elimBox.t + elimBox.h][elimBox.l];
                            if (this._tileCellBox[tileBelow].l !== elimBox.l) {
                                break;
                            }
                            tileBelow = this._tileInCell[elimBox.t + elimBox.h][elimBox.l + elimBox.w - 1];
                            var boxBelow = this._tileCellBox[tileBelow];
                            if ((boxBelow.l + boxBelow.w) === (elimBox.l + elimBox.w)) {
                                expansionDirection = "south";
                                // Expand all of the tiles below
                                for (col = elimBox.l; col < (elimBox.l + elimBox.w); col++) {
                                    if (this._tileInCell[elimBox.t][col] === tileToEliminate) {
                                        tileBelow = this._tileInCell[elimBox.t + elimBox.h][col];
                                        boxBelow = this._tileCellBox[tileBelow];
                                        boxBelow.t -= elimBox.h;
                                        boxBelow.h += elimBox.h;
                                        this._recordOccupyingTile(boxBelow, tileBelow);
                                    }
                                }
                                this._coalesceRow(elimBox.t);
                            }
                        }
                        break;
                    case "east":
                        if ((elimBox.l + elimBox.w) < this.gridSize.w) {
                            var tileToRight = this._tileInCell[elimBox.t][elimBox.l + elimBox.w];
                            if (this._tileCellBox[tileToRight].t !== elimBox.t) {
                                break;
                            }
                            tileToRight = this._tileInCell[elimBox.t + elimBox.h - 1][elimBox.l + elimBox.w];
                            var boxToRight = this._tileCellBox[tileToRight];
                            if ((boxToRight.t + boxToRight.h) === (elimBox.t + elimBox.h)) {
                                expansionDirection = "east";
                                // Expand all tiles to right
                                for (row = elimBox.t; row < (elimBox.t + elimBox.h); row++) {
                                    if (this._tileInCell[row][elimBox.l] === tileToEliminate) {
                                        tileToRight = this._tileInCell[row][elimBox.l + elimBox.w];
                                        boxToRight = this._tileCellBox[tileToRight];
                                        boxToRight.l -= elimBox.w;
                                        boxToRight.w += elimBox.w;
                                        this._recordOccupyingTile(boxToRight, tileToRight);
                                    }
                                }
                                this._coalesceColumn(elimBox.l);
                            }
                        }
                        break;
                    case "west":
                        if (elimBox.l > 0) {
                            var tileToLeft = this._tileInCell[elimBox.t][elimBox.l - 1];
                            if (this._tileCellBox[tileToLeft].t !== elimBox.t) {
                                break;
                            }
                            tileToLeft = this._tileInCell[elimBox.t + elimBox.h - 1][elimBox.l - 1];
                            var boxToLeft = this._tileCellBox[tileToLeft];
                            if ((boxToLeft.t + boxToLeft.h) === (elimBox.t + elimBox.h)) {
                                expansionDirection = "west";
                                // Expand all tiles to left
                                for (row = elimBox.t; row < (elimBox.t + elimBox.h); row++) {
                                    if (this._tileInCell[row][elimBox.l] === tileToEliminate) {
                                        tileToLeft = this._tileInCell[row][elimBox.l - 1];
                                        boxToLeft  = this._tileCellBox[tileToLeft];
                                        boxToLeft.w += elimBox.w;
                                        this._recordOccupyingTile(boxToLeft, tileToLeft);
                                    }
                                }
                                this._coalesceColumn(elimBox.l - 1);
                            }
                        }
                }
            }

            if (expansionDirection) {
                eliminateInfo = this._children[tileToEliminate];
                this._tileCount--;
                if (tileToEliminate < heir) {
                    this._children[tileToEliminate] = this._children[heir];
                    this._tileCellBox[tileToEliminate] = this._tileCellBox[heir];
                    this._recordOccupyingTile(this._tileCellBox[tileToEliminate], tileToEliminate);
                    tile = heir;
                } else {
                    this._children[tileToEliminate] = null;
                    tile = tileToEliminate;
                }
                // Renumber the remaining tiles
                for ( ; tile < this._tileCount; tile++)
                {
                    this._children[tile] = this._children[tile + 1];
                    this._tileCellBox[tile] = this._tileCellBox[tile + 1];
                    this._recordOccupyingTile(this._tileCellBox[tile], tile);
                }
                // Put info about the child being eliminated at the end of the array.  The subsequent
                // remove call will set this entry to null.
                this._children[this._tileCount] = eliminateInfo;
                if (eliminateInfo) {
                    this.removeChildInTile(this._tileCount, false);
                }
                this._updateSplitterVisibility();
                this.layout();
                this._flushPendingNotifications();
            }
            return eliminateInfo ? eliminateInfo.widget : null;
        },

        mergeTopBottom: function (row, col) {
            var aboveTile     = this._tileInCell[row][col];
            var belowTile     = this._tileInCell[row + 1][col];
            var externalAbove = this._convertToExternalTile(aboveTile);
            var externalBelow = this._convertToExternalTile(belowTile);
            var keepTile, removeTile, mergeData;
            if (this._children[belowTile] &&
                (!this._children[aboveTile] || this._children[belowTile].properties.get(LayoutChildProperty.IS_SELECTED))) {
                keepTile = belowTile;
                removeTile = aboveTile
            } else {
                keepTile = aboveTile;
                removeTile = belowTile
            }
            mergeData = this._prepareForMerge(keepTile, [removeTile]);
            this._mergeTiles (keepTile, removeTile);
            this._coalesceRow(row);
            this._updateSplitterVisibility();
            this._reactToMerge(keepTile, [removeTile], mergeData);
            this.layout();
            this.emit(TILES_MERGED, externalAbove, externalBelow);
            this._pendingStateChangeNotification = true;
            this._flushPendingNotifications();
        },

        mergeLeftRight: function (row, col) {
            var leftTile      = this._tileInCell[row][col];
            var rightTile     = this._tileInCell[row][col + 1];
            var externalLeft  = this._convertToExternalTile(leftTile);
            var externalRight = this._convertToExternalTile(rightTile);
            var keepTile, removeTile, mergeData;
            if (this._children[rightTile] &&
                (!this._children[leftTile] || this._children[rightTile].properties.get(LayoutChildProperty.IS_SELECTED))) {
                keepTile = rightTile;
                removeTile = leftTile;
            } else {
                keepTile = leftTile;
                removeTile = rightTile;
            }
            mergeData = this._prepareForMerge(keepTile, [removeTile]);
            this._mergeTiles (keepTile, removeTile);
            this._coalesceColumn(col);
            this._updateSplitterVisibility();
            this._reactToMerge(keepTile, [removeTile], mergeData);
            this.layout();
            this.emit(TILES_MERGED, externalLeft, externalRight);
            this._pendingStateChangeNotification = true;
            this._flushPendingNotifications();
        },

        splitTopBottom: function (splitRow, splitCol) {
            var splitTile    = this._tileInCell[splitRow][splitCol];
            var externalTile = this._convertToExternalTile(splitTile);
            var splitBox     = this._tileCellBox[splitTile];
            var newBox       = lang.clone(splitBox);
            var splitData    = this._prepareForSplit(splitTile, "vertical");
            var insertedRow;
            var row, col, tile;
            if (splitBox.h > 1) {
                // The tile spans more than one row.  Split it along an existing row boundary.
                newBox.t   = (splitRow > splitBox.t) ? splitRow : splitRow + 1;
                splitBox.h = newBox.t - splitBox.t;
                newBox.h  -= splitBox.h;
            } else {
                // The tile is only one row high.  A new row must be added in order to split it.
                insertedRow = true;
                newBox.t    = splitRow + 1;
                newBox.h    = 1;
                var newGridSize = {w: this.gridSize.w, h: this.gridSize.h + 1};
                var newTileInCell = [];
                newTileInCell.length = newGridSize.h;
                var newRowTop = [];
                newRowTop.length = newGridSize.h + 1;

                // Copy the data for rows prior to the inserted row
                for (row = 0; row <= splitRow; row++) {
                    newTileInCell[row] = this._tileInCell[row];
                    newRowTop    [row] = this._rowTop    [row];
                }

                // Insert data for the new row
                newTileInCell[row] = [];
                newTileInCell[row].length = this.gridSize.w;
                var previousTile  = -1;
                var precedingTile = -1;
                for (col = 0; col < this.gridSize.w; col++) {
                    tile = this._tileInCell[row - 1][col];
                    if (tile !== splitTile && tile !== previousTile) {
                        this._tileCellBox[tile].h++;
                    }
                    newTileInCell[row][col] = tile;
                    previousTile = tile;
                    if (col < splitCol) {
                        precedingTile = tile;
                    }
                }
                newRowTop[row] = (this._rowTop[row - 1] + this._rowTop[row]) >> 1;

                // Reposition subsequent tile rectangles
                for (tile = precedingTile + 1; tile < this._tileCount; tile++) {
                    if (tile !== splitTile && this._tileCellBox[tile].t > splitRow)
                       this._tileCellBox[tile].t++;
                }

                // Copy the data for rows after the eliminated rows
                for (row++; row < newGridSize.h; row++) {
                    newTileInCell[row] = this._tileInCell[row - 1];
                    newRowTop    [row] = this._rowTop    [row - 1];
                }
                newRowTop[newGridSize.h] = this._rowTop[this.gridSize.h];

                var oldGridSize  = this.gridSize;
                this.gridSize    = newGridSize;
                this._tileInCell = newTileInCell;
                this._rowTop     = newRowTop;
                this._rowWeight  = [];
                this._rowWeight.length = this.gridSize.h;
                this._clearTileConversionTables();
                this._refreshSplitters();
                this._updateWeights();
                this._queueGridSizeChangeNotification(oldGridSize, newGridSize);
            }
            this._insertTile(newBox);
            if (!insertedRow) {
                this._coalesceRow(splitRow);
            }
            this._updateSplitterVisibility();
            this._reactToSplit(splitTile, "vertical", splitData);
            this.layout();
            this.emit(TILE_SPLIT, externalTile, HORIZONTAL);
            this._pendingStateChangeNotification = true;
            this._flushPendingNotifications();
        },

        splitLeftRight: function (splitRow, splitCol) {
            var splitTile    = this._tileInCell[splitRow][splitCol];
            var externalTile = this._convertToExternalTile(splitTile);
            var splitBox     = this._tileCellBox[splitTile];
            var newBox       = lang.clone(splitBox);
            var splitData    = this._prepareForSplit(splitTile, "horizontal");
            var insertedColumn;
            var row, col, tile;
            if (splitBox.w > 1) {
                // The tile spans more than one column.  Split it along an existing column boundary.
                newBox.l   = (splitCol > splitBox.l) ? splitCol : splitCol + 1;
                splitBox.w = newBox.l - splitBox.l;
                newBox.w  -= splitBox.w;
            } else {
                // The tile is only one column wide.  A new column must be added in order to split it.
                insertedColumn = true;
                newBox.l = splitCol + 1;
                newBox.w = 1;

                var newGridSize = {w: this.gridSize.w + 1, h: this.gridSize.h};

                // Insert cell data for the new column
                var widenedTile = -1;
                for (row = 0; row < this.gridSize.h; row++) {
                    var newTileInCell = [];
                    newTileInCell.length = newGridSize.w;
                    // Copy the data for rows prior to the inserted row
                    for (col = 0; col <= splitCol; col++) {
                        newTileInCell[col] = this._tileInCell[row][col];
                    }
                    // Insert data for the new row
                    tile = this._tileInCell[row][col - 1];
                    newTileInCell[col] = tile;
                    // Widen the tile rect but only if this is the first encounter.
                    if (tile !== splitTile && tile !== widenedTile) {
                        this._tileCellBox[tile].w++;
                        widenedTile = tile;
                    }
                    var previousTile = tile;
                    // Copy the data for rows after the eliminated rows
                    for (col++; col < newGridSize.w; col++) {
                        tile = this._tileInCell[row][col - 1];
                        newTileInCell[col] = tile;
                        // Reposition the subsequent rectangles
                        if (tile !== previousTile && this._tileCellBox[tile].t === row)
                            this._tileCellBox[tile].l++;
                        previousTile = tile;
                    }
                    this._tileInCell[row] = newTileInCell;
                }

                // Insert the new column left position
                var newColumnLeft = [];
                newColumnLeft.length = newGridSize.w + 1;
                for (col = 0; col <= splitCol; col++) {
                    newColumnLeft[col] = this._columnLeft[col];
                }
                newColumnLeft[col] = (this._columnLeft[col - 1] + this._columnLeft[col]) >> 1;
                for (col++; col <= newGridSize.w; col++) {
                    newColumnLeft[col] = this._columnLeft[col - 1];
                }
                this._columnLeft    = newColumnLeft;
                this._columnWeight  = [];
                this._columnWeight.length = newGridSize.w;

                var oldGridSize = this.gridSize;
                this.gridSize = newGridSize;
                this._clearTileConversionTables();
                this._refreshSplitters();
                this._updateWeights();
                this._queueGridSizeChangeNotification(oldGridSize, newGridSize);
            }
            this._insertTile(newBox);
            if (!insertedColumn) {
                this._coalesceColumn(splitCol);
            }
            this._updateSplitterVisibility();
            this._reactToSplit(splitTile, "horizontal", splitData);
            this.layout();
            this.emit(TILE_SPLIT, externalTile, VERTICAL);
            this._pendingStateChangeNotification = true;
            this._flushPendingNotifications();
        },

        _insertTile: function (newTileBox) {
            var maxTileCount = this.gridSize.w * this.gridSize.h;
            var newChildren = [];
            newChildren.length = maxTileCount;
            var newTileCellBox = [];
            newTileCellBox.length = maxTileCount;

            // Determine the tile that precedes the new tile
            var leftTile  = -1;
            var aboveTile = -1;
            if (newTileBox.l > 0) {
                leftTile = this._tileInCell[newTileBox.t][newTileBox.l - 1];
            }
            if (newTileBox.t > 0) {
                aboveTile = this._tileInCell[newTileBox.t - 1][this.gridSize.w - 1];
            }
            var precedingTile = (leftTile > aboveTile) ? leftTile : aboveTile;

            // Copy data up to and including that for the preceding tile
            var tile;
            for (tile = 0; tile <= precedingTile; tile++) {
                newChildren   [tile] = this._children   [tile];
                newTileCellBox[tile] = this._tileCellBox[tile];
            }

            // Insert the new data
            newTileCellBox[tile] = newTileBox;
            this._recordOccupyingTile(newTileBox, tile);

            // Copy the remaining data
            for (; tile < this._tileCount; tile++) {
                newChildren   [tile + 1] = this._children   [tile];
                newTileCellBox[tile + 1] = this._tileCellBox[tile];
                this._recordOccupyingTile(this._tileCellBox[tile], tile + 1);
            }
            this._tileCellBox = newTileCellBox;
            this._children    = newChildren;
            this._tileCount++;
        },

        _mergeTiles: function (tileToKeep, tileToRemove) {
            // Remove the specified component (if any)
            if (this._children[tileToRemove]) {
                this.removeChildInTile(tileToRemove, false);
            }

            // Update tile <-> cell mapping
            var lowerTile = (tileToRemove < tileToKeep) ? tileToRemove : tileToKeep;
            this._tileCellBox[lowerTile] = Geometry.unionBox(this._tileCellBox[tileToRemove],
                                                             this._tileCellBox[tileToKeep]);
            this._recordOccupyingTile(this._tileCellBox[lowerTile], lowerTile);
            this._children[lowerTile] = this._children[tileToKeep];

            // Update the mappings for subsequent tiles
            this._tileCount--;

            var upperTile = (tileToRemove > tileToKeep) ? tileToRemove : tileToKeep;
            var tile;
            for (tile = upperTile; tile < this._tileCount; tile++) {
                this._children[tile] = this._children[tile + 1];
                this._tileCellBox[tile] = this._tileCellBox[tile + 1];
                this._recordOccupyingTile(this._tileCellBox[tile], tile);
            }
            this._children[this._tileCount] = null;
        },

        /**
         * Eliminates unnecessary rows, that is rows that are spanned equally by a
         * tile in each column.
         *
         * @param row {number} the first row to coalesce
         */
        _coalesceRow: function (row) {
            var tile = this._tileInCell[row][0];
            var startRow    = this._tileCellBox[tile].t;
            var endRow      = startRow + this._tileCellBox[tile].h;
            var rowsSpanned = endRow - startRow;
            var col, tileStart, tileEnd;
            for (col = 1; col < this.gridSize.w && rowsSpanned > 1; col++) {
                tile = this._tileInCell[row][col];
                tileStart = this._tileCellBox[tile].t;
                if (tileStart > startRow) {
                    startRow = tileStart;
                }
                tileEnd = tileStart + this._tileCellBox[tile].h;
                if (tileEnd < endRow) {
                    endRow = tileEnd;
                }
                rowsSpanned = endRow - startRow;
            }
            if (rowsSpanned > 1) {
                // All columns spanning the specified row also span at least one more
                // row.  Update the data structures to eliminate the unnecessary rows

                // Reduce the height of the cell boxes spanning the rows that are being eliminated
                var previous = -1;
                for (col = 0; col < this.gridSize.w; col++) {
                    tile = this._tileInCell[startRow][col];
                    if (tile !== previous) {
                        this._tileCellBox[tile].h -= (rowsSpanned - 1);
                        previous = tile;
                    }
                }

                var newGridSize = {w: this.gridSize.w, h: this.gridSize.h - rowsSpanned + 1};
                var newTileInCell = [];
                newTileInCell.length = newGridSize.h;
                var newRowTop = [];
                newRowTop.length = newGridSize.h + 1;
                // Copy the data for rows prior to the eliminated rows
                for (row = 0; row <= startRow; row++) {
                    newTileInCell[row] = this._tileInCell[row];
                    newRowTop    [row] = this._rowTop    [row];
                }
                // Copy the data for rows after the eliminated rows
                var prevTileInCol = [];
                prevTileInCol.length = newGridSize.w;
                for (col = 0; col < prevTileInCol.length; col++) {
                    prevTileInCol[col] = -1;
                }
                for ( ; row < newGridSize.h; row++) {
                    newTileInCell[row] = this._tileInCell[row + rowsSpanned - 1];
                    newRowTop    [row] = this._rowTop    [row + rowsSpanned - 1];
                    // Adjust the y position of the cell rectangles that follow the
                    // eliminated rows.
                    var prevTileInRow = -1;
                    for (col = 0; col < this.gridSize.w; col++) {
                        tile = newTileInCell[row][col];
                        if (tile !== prevTileInRow && tile !== prevTileInCol[col] && tile !== this._tileInCell[startRow][col]) {
                            this._tileCellBox[tile].t -= rowsSpanned - 1;
                            prevTileInRow = tile;
                            prevTileInCol[col] = tile;
                        }
                    }
                }
                newRowTop[newGridSize.h] = this._rowTop[this.gridSize.h];
                var oldGridSize  = this.gridSize;
                this.gridSize    = newGridSize;
                this._tileInCell = newTileInCell;
                this._rowTop     = newRowTop;
                this._clearTileConversionTables();
                this._refreshSplitters();
                this._updateWeights();
                this._queueGridSizeChangeNotification(oldGridSize, newGridSize);
            }
        },

        /**
         * Eliminates unnecessary columns, that is columns that are spanned equally by
         * by a tile in each row.
         *
         * @param col {number} the first column to coalesce
         */
        _coalesceColumn: function (col) {
            var tile = this._tileInCell[0][col];
            var startColumn    = this._tileCellBox[tile].l;
            var endColumn      = startColumn + this._tileCellBox[tile].w;
            var columnsSpanned = endColumn - startColumn;
            var row, tileStart, tileEnd;
            for (row = 1; row < this.gridSize.h && columnsSpanned > 1; row++)
            {
                tile = this._tileInCell[row][col];
                tileStart = this._tileCellBox[tile].l;
                if (tileStart > startColumn) {
                    startColumn = tileStart;
                }
                tileEnd = tileStart + this._tileCellBox[tile].w;
                if (tileEnd < endColumn) {
                    endColumn = tileEnd;
                }
                columnsSpanned = endColumn - startColumn;
            }
            if (columnsSpanned > 1) {
                // All rows spanning the specified column also span at least one more
                // column.  Update the data structures to eliminate the unnecessary columns

                // Reduce the width of the cell rectangles spanning the columns that
                // are being eliminated
                var previous = -1;
                for (row = 0; row < this.gridSize.h; row++) {
                    tile = this._tileInCell[row][startColumn];
                    if (tile !== previous) {
                        this._tileCellBox[tile].w -= (columnsSpanned - 1);
                        previous = tile;
                    }
                }
                var newGridSize = {w: this.gridSize.w - columnsSpanned + 1, h: this.gridSize.h};

                // Column left coordinates
                var newColumnLeft  = [];
                newColumnLeft.length =newGridSize.w + 1;
                // Copy data for columns prior to the eliminated columns
                for (col = 0; col <= startColumn; col++) {
                    newColumnLeft[col] = this._columnLeft[col];
                }
                // Copy data for columns subsequent to the eliminated columns
                for ( ; col <= newGridSize.w; col++) {
                    newColumnLeft[col] = this._columnLeft[col + columnsSpanned - 1];
                }
                this._columnLeft = newColumnLeft;

                // Cell to tile mapping
                var prevTileInCol = [];
                prevTileInCol.length = newGridSize.w;
                for (col = startColumn; col < prevTileInCol.length; col++) {
                    prevTileInCol[col] = -1;
                }
                for (row = 0; row < this.gridSize.h; row++) {
                    var newTileInCell = [];
                    newTileInCell.length = newGridSize.w;
                    // Copy data for columns prior to the eliminated columns
                    for (col = 0; col <= startColumn; col++) {
                        newTileInCell[col] = this._tileInCell[row][col];
                    }
                    // Copy data for columns prior to the eliminated columns
                    var prevTileInRow = -1;
                    for ( ; col < newGridSize.w; col++) {
                        newTileInCell[col] = this._tileInCell[row][col + columnsSpanned - 1];
                        // Adjust the x position of the cell rectangles that follow the
                        // eliminated columns.
                        tile = newTileInCell[col];
                        if (tile !== prevTileInRow && tile !== prevTileInCol[col] && tile !== this._tileInCell[row][startColumn]) {
                            this._tileCellBox[tile].l -= columnsSpanned - 1;
                            prevTileInCol[col] = tile;
                            prevTileInRow = tile;
                        }
                    }
                    this._tileInCell[row] = newTileInCell;
                }
                var oldGridSize = this.gridSize;
                this.gridSize = newGridSize;
                this._clearTileConversionTables();
                this._refreshSplitters();
                this._updateWeights();
                this._queueGridSizeChangeNotification(oldGridSize, newGridSize);
            }
        },

        _recordOccupyingTile: function (cellBox, tile) {
            this._recordTileInCell(this._tileInCell, cellBox, tile);
            this._clearTileConversionTables();
        },

        _recordTileInCell: function (tileInCell, cellBox, tile) {
            var endRow = cellBox.t + cellBox.h;
            var endCol = cellBox.l + cellBox.w;
            var  row, col;
            for (row = cellBox.t; row < endRow; row++) {
                for (col = cellBox.l; col < endCol; col++) {
                    tileInCell[row][col] = tile;
                }
            }
        },

        _updateWeights: function () {
            var visibleSplitterThickness = this._getVisibleSplitterThickness();
            var totalHeight = this._rowTop[this.gridSize.h] - this._rowTop[0] - (this.gridSize.h - 1) * visibleSplitterThickness;
            var totalWidth = this._columnLeft[this.gridSize.w] - this._columnLeft[0] - (this.gridSize.w - 1) * visibleSplitterThickness;
            var row, col;
            if (totalHeight > 0) {
                for (row = 0; row < this.gridSize.h; row++) {
                    this._rowWeight[row] = (this._rowTop[row + 1] - this._rowTop[row] - (row ? visibleSplitterThickness : 0)) / totalHeight;
                }
            } else {
                this._rowWeight = this._getDefaultWeights(this.gridSize.h);
            }

            if (totalWidth > 0) {
                for (col = 0; col < this.gridSize.w; col++) {
                    this._columnWeight[col] = (this._columnLeft[col + 1] - this._columnLeft[col] - (col ? visibleSplitterThickness : 0)) / totalWidth;
                }
            } else {
                this._columnWeight = this._getDefaultWeights(this.gridSize.w);
            }
            this.notifyStateWatchers(ROW_WEIGHTS, [], this.get(ROW_WEIGHTS));
            this.notifyStateWatchers(COLUMN_WEIGHTS, [], this.get(COLUMN_WEIGHTS));
        },

        _updateSplitterVisibility: function () {
            var row, col, prevTile, prevBox, tile, box, splitter;

            // Hide vertical splitters that divide columns sections containing the same tile
            for (row = 0; row < this.gridSize.h; row++) {
                prevTile = this._tileInCell[row][0];
                for (col = 1; col < this.gridSize.w; col++) {
                    tile = this._tileInCell[row][col];
                    splitter = this._verticalSplitters[col-1][row];
                    if (tile === prevTile) {
                        domStyle.set(splitter, {visibility: "hidden"});
                    } else {
                        domStyle.set(splitter, {visibility: "visible"});
                        prevBox = this._tileCellBox [prevTile];
                        box = this._tileCellBox [tile];
                        if (prevBox.t === box.t && prevBox.h === box.h &&
                            (box.h === 1 || row === box.t + Math.floor((box.h - 1) / 2))) {
                            domClass.add(splitter, "removable");
                            if ((box.h & 1) === 0) {
                                domClass.add(splitter, "shiftDot");
                            } else {
                                domClass.remove(splitter, "shiftDot");
                            }
                        } else {
                            domClass.remove(splitter, "removable");
                        }
                        prevTile = tile;
                    }
                }
            }

            // Hide horizontal splitters that divide rows sections containing the same tile
            for (col = 0; col < this.gridSize.w; col++) {
                prevTile = this._tileInCell[0][col];
                for (row = 1; row < this.gridSize.h; row++) {
                    tile = this._tileInCell[row][col];
                    splitter = this._horizontalSplitters[row-1][col];
                    if (tile === prevTile) {
                        domStyle.set(splitter, {visibility: "hidden"});
                    } else {
                        domStyle.set(splitter, {visibility: "visible"});
                        prevBox = this._tileCellBox [prevTile];
                        box = this._tileCellBox [tile];
                        if (prevBox.l === box.l && prevBox.w === box.w &&
                            (box.w === 1 || col === box.l + Math.floor((box.w - 1) / 2))) {
                            domClass.add(splitter, "removable");
                            if ((box.w & 1) === 0) {
                                domClass.add(splitter, "shiftDot");
                            } else {
                                domClass.remove(splitter, "shiftDot");
                            }
                        } else {
                            domClass.remove(splitter, "removable");
                        }
                        prevTile = tile;
                    }
                }
            }
        },

        _getSplitterTopLocalUpperLimit: function (splitterIndex, rowTops) {
            // Choose the upper limit to avoid violating the minimum size of any tile.
            var upperLimit = rowTops[this.gridSize.h];
            var row, col, tile, prevTile, span, minHeight, tileLimit;
            row = splitterIndex + 1;
            for (col = 0; col < this.gridSize.w; col++, prevTile = tile) {
                tile = this._tileInCell[row][col];
                // If the tile differs from that in the previous column and starts just below the splitter
                // being moved then take its minimum height into consideration.
                if (tile !== prevTile && this._tileCellBox[tile].t === row) {
                    span = this._tileCellBox[tile].h;
                    minHeight = undefined;
                    if (this._children[tile]) {
                        minHeight = this._children[tile].properties.minimumHeight;
                    }
                    if (minHeight === undefined) {
                        minHeight = this.minCellHeight * span;
                    }
                    tileLimit = rowTops[row + span] - minHeight;
                    if (tileLimit < upperLimit) {
                        upperLimit = tileLimit;
                    }
                }
            }
            return upperLimit;
        },

        _getSplitterTopGlobalUpperLimit: function (splitterIndex) {
            // Work backward from the bottom honoring all minimum heights
            var index;
            var rowTops = [];
            rowTops.length = this.gridSize.h + 1;
            rowTops[this.gridSize.h] = this._rowTop[this.gridSize.h];
            for (index = this.gridSize.h - 2; index >= splitterIndex; index--) {
                rowTops[index + 1] = this._getSplitterTopLocalUpperLimit(index, rowTops);
            }
            return rowTops[splitterIndex + 1];
        },

        _getSplitterTopLocalLowerLimit: function (splitterIndex, rowTops) {
            // Choose the lower limit to avoid violating the minimum size of any tile.
            var lowerLimit = rowTops[0];
            var row, col, tile, prevTile, span, minHeight, tileLimit;
            row = splitterIndex;
            for (col = 0; col < this.gridSize.w; col++, prevTile = tile) {
                tile = this._tileInCell[row][col];
                // If the tile differs from that in the previous column and ends just above the splitter
                // being moved then take its minimum height into consideration.
                if (tile !== prevTile) {
                    span = this._tileCellBox[tile].h;
                    if ((this._tileCellBox[tile].t + span - 1) === row) {
                        minHeight = undefined;
                        if (this._children[tile]) {
                            minHeight = this._children[tile].properties.minimumHeight;
                        }
                        if (minHeight === undefined) {
                            minHeight = this.minCellHeight * span;
                        }
                        tileLimit = rowTops[row - span + 1] + minHeight;
                        if (tileLimit > lowerLimit) {
                            lowerLimit = tileLimit;
                        }
                    }
                }
            }
            return lowerLimit;
        },

        _getSplitterTopGlobalLowerLimit: function (splitterIndex) {
            // Work from the top honoring all minimum heights
            var index;
            var rowTops = [];
            rowTops.length = this.gridSize.h + 1;
            rowTops[0] = this._rowTop[0];
            for (index = 0; index <= splitterIndex; index++) {
                rowTops[index + 1] = this._getSplitterTopLocalLowerLimit(index, rowTops);
            }
            return rowTops[splitterIndex + 1];
        },

        _getSplitterLeftLocalUpperLimit: function (splitterIndex, columnLefts) {
            // Choose the upper limit to avoid violating the minimum size of any tile.
            var upperLimit = columnLefts[this.gridSize.w];
            var row, col, tile, prevTile, span, minWidth, tileLimit;
            col = splitterIndex + 1;
            for (row = 0; row < this.gridSize.h; row++, prevTile = tile) {
                tile = this._tileInCell[row][col];
                // If the tile differs from that in the previous row and starts just after the splitter
                // being moved then take its minimum width into consideration.
                if (tile !== prevTile && this._tileCellBox[tile].l === col) {
                    span = this._tileCellBox[tile].w;
                    minWidth = undefined;
                    if (this._children[tile]) {
                        minWidth = this._children[tile].properties.minimumWidth;
                    }
                    if (minWidth === undefined) {
                        minWidth = this.minCellWidth * span;
                    }
                    tileLimit = columnLefts[col + span] - minWidth;
                    if (tileLimit < upperLimit) {
                        upperLimit = tileLimit;
                    }
                }
            }
            return upperLimit;
        },

        _getSplitterLeftGlobalUpperLimit: function (splitterIndex) {
            // Work backward from the right honoring all minimum heights
            var index;
            var columnLefts = [];
            columnLefts.length = this.gridSize.w + 1;
            columnLefts[this.gridSize.w] = this._columnLeft[this.gridSize.w];
            for (index = this.gridSize.w - 2; index >= splitterIndex; index--) {
                columnLefts[index + 1] = this._getSplitterLeftLocalUpperLimit(index, columnLefts);
            }
            return columnLefts[splitterIndex + 1];
        },

        _getSplitterLeftLocalLowerLimit: function (splitterIndex, columnLefts) {
            // Choose the lower limit to avoid violating the minimum size of any tile.
            var lowerLimit = columnLefts[0];
            var row, col, tile, prevTile, span, minWidth, tileLimit;
            col = splitterIndex;
            for (row = 0; row < this.gridSize.h; row++, prevTile = tile) {
                tile = this._tileInCell[row][col];
                // If the tile differs from that in the previous row and ends just before the splitter
                // being moved then take its minimum width into consideration.
                if (tile !== prevTile) {
                    span = this._tileCellBox[tile].w;
                    if ((this._tileCellBox[tile].l + span - 1) === col) {
                        minWidth = undefined;
                        if (this._children[tile]) {
                            minWidth = this._children[tile].properties.minimumWidth;
                        }
                        if (minWidth === undefined) {
                            minWidth = this.minCellWidth * span;
                        }
                        tileLimit = columnLefts[col - span + 1] + minWidth;
                        if (tileLimit > lowerLimit) {
                            lowerLimit = tileLimit;
                        }
                    }
                }
            }
            return lowerLimit;
        },

        _getSplitterLeftGlobalLowerLimit: function (splitterIndex) {
            // Work from the left honoring all minimum width
            var index;
            var columnLefts = [];
            columnLefts.length = this.gridSize.w + 1;
            columnLefts[0] = this._columnLeft[0];
            for (index = 0; index <= splitterIndex; index++) {
                columnLefts[index + 1] = this._getSplitterLeftLocalLowerLimit(index, columnLefts);
            }
            return columnLefts[splitterIndex + 1];
        },

        _superRemoveChild: function (child) {
            _LayoutWidget.prototype.removeChild.apply(this, [child]);
        },

        getState: function () {
            return {
                gridDimensions: this.gridSize,
                tileCount: this._tileCount,
                tileCoverage: this._getTileCoverageAttr(),
                referenceWidth: this._referenceWidth || 0,
                referenceHeight: this._referenceHeight || 0,
                columnWeights: this._columnWeight.slice(0, this.gridSize.w),
                rowWeights: this._rowWeight.slice(0, this.gridSize.h),
                columnLeft: this._columnLeft.slice(0),
                rowTop: this._rowTop.slice(0)
            };
        },

        setState: function (state) {
            var savedChildren, limit, tile, row, col;
            this.gridSize = state.gridDimensions || state.gridSize || {w: 1, h: 1};
            this._tileCount = state.tileCount || (this.gridSize.w * this.gridSize.h);
            this._referenceWidth = state.referenceWidth;
            this._referenceHeight = state.referenceHeight;
            this._columnWeight = this._assignWeights(this.gridSize.w, state.columnWeights);
            this._rowWeight = this._assignWeights(this.gridSize.h, state.rowWeights);
            if (state.columnLeft) {
                this._columnLeft = state.columnLeft.slice(0);
            } else {
                this._columnLeft = [];
                this._columnLeft.length = this.gridSize.w + 1;
                this._referenceWidth = 0;  // Force computation of column lefts upon layout
            }
            if (state.rowTop) {
                this._rowTop = state.rowTop.slice(0);
            } else {
                this._rowTop = [];
                this._rowTop.length = this.gridSize.h + 1;
                this._referenceHeight = 0;  // Force computation of row tops upon layout
            }

            if (!state.tileCellBox) {
                if (state.tileCoverage) {
                    // Clone the coverage as tileInCell
                    this._tileInCell = [];
                    this._tileInCell.length = this.gridSize.h;
                    for (row = 0; row < this.gridSize.h; row++) {
                        this._tileInCell[row] = state.tileCoverage[row].slice(0, this.gridSize.w);
                    }
                } else {
                    // Allocate the default tileInCell (one tile per cell)
                    this._tileInCell = this._allocateTileInCell(this.gridSize);
                    for (row = 0, tile = 0; row < this.gridSize.h; row++) {
                        for (col = 0; col < this.gridSize.w; col++, tile++) {
                            this._tileInCell[row][col] = tile;
                        }
                    }
                }
                this._tileCellBox = this._getTileCellBoxesFromTileInCell(this._tileInCell);
            } else {
                this._tileCellBox = [];
                this._tileCellBox.length = state.tileCellBox.length;
                for (tile = 0; tile < state.tileCellBox.length; tile++) {
                    this._tileCellBox[tile] = lang.clone(state.tileCellBox[tile]);
                }
                // Create and populate the tileInCell 2D array
                this._tileInCell = this._allocateTileInCell(this.gridSize);
                for (tile = 0; tile < this._tileCount; tile++) {
                    this._recordTileInCell(this._tileInCell, this._tileCellBox[tile], tile);
                }
            }

            // Allocate a new child array and copy existing children
            savedChildren = this._children;
            this._children = [];
            this._children.length = this._tileCount;
            limit = Math.min(savedChildren.length, this._children.length);
            for (tile = 0; tile < limit; tile++) {
                this._children[tile] = savedChildren[tile];
            }
            // Remove children for which there is no tile available
            for ( ; tile < savedChildren.length; tile++) {
                if (savedChildren[tile]) {
                    var toRemove = savedChildren[tile];
                    this._superRemoveChild(toRemove.widget);
                    this.emit(LayoutContainerEvent.CHILD_REMOVED, toRemove.widget, toRemove.properties);
                    LayoutUtils.restoreStyle(toRemove.widget);
                }
            }

            this._clearTileConversionTables();
            this._refreshSplitters();
            this._updateSplitterVisibility();
            this.layout();
        },

        _assignWeights: function (size, specifiedWeights) {
            var i, reductionFactor;
            var weights = [];
            weights.length = size;
            var cumulativeWeight = 0;
            var unspecifiedCount = 0;
            for (i = 0; i < size; i++) {
                if (specifiedWeights && specifiedWeights.length > i) {
                    weights[i] = specifiedWeights[i];
                } else {
                    weights[i] = -1;
                }
                if (weights[i] >= 0) {
                    cumulativeWeight += weights[i];
                } else {
                    unspecifiedCount++;
                }
            }
            if (cumulativeWeight > 1.0) {
                reductionFactor = 1.0 / cumulativeWeight;
                cumulativeWeight = 1.0;
            }
            if (unspecifiedCount > 0 || reductionFactor) {
                var share = unspecifiedCount > 0 ? ((1.0 - cumulativeWeight) / unspecifiedCount) : 0;
                for (i = 0; i < size; i++) {
                    if (weights[i] < 0) {
                        weights[i] = share;
                    } else if (reductionFactor) {
                        weights[i] += reductionFactor;
                    }
                }
            }
            return weights;
        },

        _allocateTileInCell: function(gridSize) {
            var row;
            var tileInCell = [];
            tileInCell.length = gridSize.h;
            for (row = 0; row < gridSize.h; row++) {
                tileInCell[row] = [];
                tileInCell[row].length = gridSize.w;
            }
            return tileInCell;
        },

        /**
         * Sets the tile coverage, i.e. the assignment of tiles, which may span multiple rows and/or columns to the grid.
         * Tiles must be rectangular and appear in ascending order as the grid is traversed in row major order.  The
         * coverage is specified as a matrix (more accurately an array of arrays).  The grid size will be inferred from
         * the array dimensions.
         *
         * @param coverage {Array} array of arrays identifying the tile associated with each grid cell in row major order
         */
        _setTileCoverageAttr: function (coverage) {
            if (!this._areCoveragesEqual(coverage, this._tileInCell)) {
                var state = this._convertTileCoverageToState(coverage);
                var validationResult = TileContainer.validateState(state);
                if (!validationResult.isValid) {
                    Log.error(validationResult.failureMessage);
                } else {
                    this._isSettingTileCoverage = true;
                    var saveInfo = this._prepareForGridSizeChange(state.gridDimensions);
                    var savedChildren = this._children;
                    this.setState(state);
                    this._redistributeChildren(savedChildren);
                    this._reactToGridSizeChange(saveInfo);
                    this.notifyStateWatchers(TILE_COVERAGE, {}, this._tileInCell);
                    this._isSettingTileCoverage = false;
                }
            }
        },

        /**
         * @returns {Array} array of arrays identifying the tile associated with each grid cell in row major order
         */
        _getTileCoverageAttr: function () {
            var coverage = [];
            var row;
            coverage.length = this._tileInCell.length;
            for (row = 0; row < this._tileInCell.length; row++) {
                coverage[row] = this._tileInCell[row].slice(0, this.gridSize.w);
            }
            return coverage;
        },

        _areCoveragesEqual: function (coverage1, coverage2) {
            if (coverage1.length !== coverage2.length) {
                return false;
            }
            var row, col;
            for (row = 0; row < coverage1.length; row++) {
                if (coverage1[row].length !== coverage2[row].length) {
                    return false;
                }
                for (col = 0; col < coverage1[row].length; col++) {
                    if (coverage1[row][col] !== coverage2[row][col]) {
                        return false;
                    }
                }
            }
            return true;
        },

        _convertTileCoverageToState: function (coverage) {
            var state = {
                gridDimensions: {h: coverage.length, w: coverage[0].length},
                tileCoverage: coverage
            };
            state.tileCount = this._getTileCellBoxesFromTileInCell(coverage).length;
            return state;
        },

        _getTileCellBoxesFromTileInCell: function (coverage) {
            var tileCellBox = [];
            var row, col, tile, row2, col2, w, h;
            for (row = 0; row < coverage.length; row++) {
                for (col = 0; col < coverage[row].length; col++) {
                    tile = coverage[row][col];
                    if (tile >= tileCellBox.length) {
                        // Find the tile boundaries
                        for (row2 = row + 1, h = 1; row2 < coverage.length && coverage[row2][col] === tile; row2++, h++) {}
                        for (col2 = col + 1, w = 1; col2 < coverage[row].length && coverage[row][col2] === tile; col2++, w++) {}
                        tileCellBox.push({l: col, t: row, w: w, h: h});
                    }
                }
            }
            return tileCellBox;
        },

        _getTileInCellFromTileCellBoxes: function (gridSize, tileCellBoxes) {
            var tileInCell = this._allocateTileInCell(gridSize);
            for (var tile = 0; tile < this._tileCount; tile++) {
                this._recordTileInCell(tileInCell, tileCellBoxes[tile], tile);
            }
            return tileInCell;
        },

        _queueGridSizeChangeNotification: function (oldSize, newSize) {
            if (oldSize.w !== newSize.w || oldSize.h !== newSize.h) {
                this._pendingGridSizeNotification = {oldSize: oldSize, newSize: newSize};
            }
        },

        _flushPendingNotifications: function () {
            var that = this;
            var gridSizeNotificationSent;
            if (this._pendingGridSizeNotification) {
                this.notifyStateWatchers(GRID_SIZE, this._pendingGridSizeNotification.oldSize, this._pendingGridSizeNotification.newSize);
                this.notifyStateWatchers(TILE_COVERAGE, {}, this._tileInCell);
                delete this._pendingGridSizeNotification;
                gridSizeNotificationSent = true;
            }
            if (this._pendingStateChangeNotification || gridSizeNotificationSent) {
                delete this._pendingStateChangeNotification;
                setTimeout(function () {
                    that.emit(LayoutContainerEvent.STATE_CHANGED);
                    that.notifyStateWatchers(TILE_COVERAGE, {}, that._tileInCell);
                }, 0);
            }
        },

        // Subclasses may override to perform pre-size change work
        _prepareForGridSizeChange: function (newGridSize) {
        },

        // Subclasses may override to perform post-size change work
        _reactToGridSizeChange: function (changeData) {
        },

        // Subclasses may override to perform pre-merge work
        _prepareForMerge: function (tileToKeep, tilesToRemove) {
        },

        // Subclasses may override to perform post-merge work
        _reactToMerge: function (tileToKeep, tilesToRemove, mergeData) {
        },

        // Subclasses may override to perform pre-split work
        _prepareForSplit: function (tile, splitAlong) {
        },

        // Subclasses may override to perform post-split work
        _reactToSplit: function (tile, splitAlong, splitData) {
        }
    });

    TileContainer.GRID_SIZE = GRID_SIZE;
    TileContainer.ROW_WEIGHTS = ROW_WEIGHTS;
    TileContainer.COLUMN_WEIGHTS = COLUMN_WEIGHTS;
    TileContainer.TILE_COVERAGE = TILE_COVERAGE;
    TileContainer.ROW_MAJOR = ROW_MAJOR;
    TileContainer.COLUMN_MAJOR = COLUMN_MAJOR;
    TileContainer.VERTICAL = VERTICAL;
    TileContainer.HORIZONTAL = HORIZONTAL;
    TileContainer.TILE_SPLIT = TILE_SPLIT;
    TileContainer.TILES_MERGED = TILES_MERGED;

    /**
     * Determines if two states are similar.  State are considered similar of they have the same grid, spans,
     * row and column weights.  The specific for and column widths need not be the same.
     *
     * @param {Object} state1
     * @param {Object} state2
     * @return {Boolean} true if the given states are similar
     */
    TileContainer.areStatesSimilar = function (state1, state2) {
        if (state1.gridSize.w !== state2.gridSize.w ||
            state1.gridSize.h !== state2.gridSize.h ||
            state1.tileCount !== state2.tileCount) {
            return false;
        }
        var tile, row, col;
        for (tile = 0; tile < state1.tileCount; tile++) {
            if (!Geometry.areBoxesEqual(state1.tileCellBox[tile], state2.tileCellBox[tile])) {
                return false;
            }
        }
        for (col = 0; col < state1.columnWeights; col++) {
            if (Math.abs(state1.columnWeights[col] - state2.columnWeights[col]) > 0.005) {
                return false;
            }
        }

        for (row = 0; row < state1.rowWeights.length; row++) {
            if (Math.abs(state1.rowWeights[row] - state2.rowWeights[row]) > 0.005) {
                return false;
            }
        }
        return true;
    };

    TileContainer.validateState = function (state) {
        var result = {
            isValid: true
        };
        var gridSize = state.gridDimensions || state.gridSize;
        if (state.tileCount <= 0) {
            result.isValid = false;
            result.failureMessage = "tile count must be > 0";
            return result;
        }
        if (state.tileCount > gridSize.w * gridSize.h) {
            result.isValid = false;
            result.failureMessage = "tileCount must not exceed cell count";
            return result;
        }
        if (state.columnLeft) {
            if (state.columnLeft.length != gridSize.w + 1) {
                result.isValid = false;
                result.failureMessage = "columnLeft must include entry for each column plus 1";
                return result;
            }
        }
        if (state.rowTop) {
            if (state.rowTop.length != gridSize.h + 1) {
                result.isValid = false;
                result.failureMessage = "rowTop must include entry for each row plus 1";
                return result;
            }
        }
        if (state.tileCoverage) {
            if (state.tileCoverage.length != gridSize.h) {
                result.isValid = false;
                result.failureMessage = "tileCoverage dimensions must match grid dimensions";
                return result;
            }
            for (var row = 0; row < state.tileCoverage.length; row++ ) {
                if (state.tileCoverage[row].length != gridSize.w) {
                    result.isValid = false;
                    result.failureMessage = "tileCoverage dimensions must match grid dimensions";
                    return result;
                }
                for (var col = 0; col < state.tileCoverage[row].length; col++) {
                    var tile = state.tileCoverage[row][col];
                    if (tile < 0 || tile >= state.tileCount) {
                        result.isValid = false;
                        result.failureMessage = "tileCoverage value not in range 0 - (tileCount - 1)";
                        return result;
                    }
                }
            }
        }
        return result;
    };

    return TileContainer;
});